<template>
  <!-- MBR063 Booking — member_app_UI/song_fa_members_bookings_mobile -->
  <div class="page-header align-items-start min-vh-100 mbr-booking">
    <span class="mask bg-white"></span>
    <div class="container p-0">
      <!-- MBR064 header + tabs: same three tabs as before; active state follows `type`
           instead of jQuery toggling .active on #nav_item_N -->
      <header class="mbr-booking__bar">
        <div class="mbr-booking__head">
          <button type="button" class="mbr-booking__back" :aria-label="$t('common.back')" @click="goBack()">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="m15 18-6-6 6-6" />
            </svg>
          </button>
          <h1 class="mbr-booking__title">{{ $t('booking.title') }}</h1>
        </div>
        <nav class="mbr-booking__segment" :aria-label="$t('booking.sections_aria')">
          <button
            v-for="tab in tabs"
            :key="tab.type"
            type="button"
            class="mbr-booking__seg"
            :class="{ 'mbr-booking__seg--active': type == tab.type }"
            @click="changeType(tab.type, tab.historyType)"
          >
            {{ tab.label }}
          </button>
        </nav>
      </header>

      <main v-if="isLoadDone" class="mbr-booking__body pb-90">
        <template v-if="type == 1">
          <!-- MBR114 branch selector: custom dropdown panel instead of a native <select>,
               which the browser rendered as a full-width unstyled OS list -->
          <div v-if="branches.length" ref="branchPicker" class="mbr-booking__branch-wrap">
            <button
              type="button"
              class="mbr-booking__branch"
              :class="{ 'mbr-booking__branch--open': showBranchPicker }"
              :aria-label="$t('booking.select_branch')"
              aria-haspopup="listbox"
              :aria-expanded="showBranchPicker ? 'true' : 'false'"
              @click="showBranchPicker = !showBranchPicker"
            >
              <svg class="mbr-booking__branch-pin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <path d="M12 2a8 8 0 0 0-8 8c0 5.25 8 12 8 12s8-6.75 8-12a8 8 0 0 0-8-8z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              <span class="mbr-booking__branch-name">{{ branchName || $t('booking.select_branch') }}</span>
              <svg class="mbr-booking__branch-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>

            <transition name="mbr-booking-drop">
              <ul v-if="showBranchPicker" class="mbr-booking__branch-list" role="listbox" :aria-label="$t('booking.select_branch')">
                <li v-for="branch in branches" :key="branch.value" role="none">
                  <button
                    type="button"
                    role="option"
                    class="mbr-booking__branch-option"
                    :class="{ 'mbr-booking__branch-option--active': branch.value == branchIdSelect }"
                    :aria-selected="branch.value == branchIdSelect ? 'true' : 'false'"
                    @click="selectBranch(branch.value)"
                  >
                    <span>{{ branch.label }}</span>
                    <svg v-if="branch.value == branchIdSelect" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                      <path stroke-linecap="round" stroke-linejoin="round" d="m5 12 5 5L20 7" />
                    </svg>
                  </button>
                </li>
              </ul>
            </transition>
          </div>

          <p class="mbr-booking__eyebrow">{{ $t('booking.available_venues') }}</p>

          <article
            v-for="(item, index) in venues"
            :key="index"
            class="mbr-booking__venue"
            @click="selectVenue(item.id)"
          >
            <div class="mbr-booking__venue-img">
              <img :src="item.image" :alt="item.name" />
            </div>
            <div class="mbr-booking__venue-row">
              <h3 class="mbr-booking__venue-name">{{ item.name }}</h3>
              <span v-if="fromPrice(item)" class="mbr-booking__venue-price">{{ fromPrice(item) }}</span>
            </div>
            <button type="button" class="mbr-booking__book" @click.stop="selectVenue(item.id)">
              <span>{{ $t('booking.book') }}</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M5 12h14m-6-6 6 6-6 6" />
              </svg>
            </button>
          </article>

          <section v-if="!venues.length" class="mbr-booking__empty">
            <span class="mbr-booking__empty-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </span>
            <p class="mbr-booking__empty-title">{{ $t('booking.no_venues') }}</p>
            <p class="mbr-booking__empty-text">
              {{ $t('booking.no_venues_text') }}
            </p>
            <button
              v-if="branches.length > 1"
              type="button"
              class="mbr-booking__empty-btn"
              @click="openBranchSelect"
            >
              {{ $t('booking.change_branch') }}
            </button>
          </section>
        </template>

        <!-- MBR065 Active / History: same fields as before -->
        <template v-else>
          <article v-for="(item, index) in bookingList" :key="index" class="mbr-booking__card">
            <div class="mbr-booking__card-top">
              <span class="mbr-booking__code">#{{ item.booking_code }}</span>
              <span class="mbr-booking__status" :class="{ 'mbr-booking__status--done': type == 3 }">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" d="m5 12 5 5L20 7" />
                </svg>
                {{ type == 3 ? $t('booking.completed') : $t('booking.upcoming') }}
              </span>
            </div>
            <ul class="mbr-booking__meta">
              <li>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                {{ formatDate(item.booking_date) }}
              </li>
              <li>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                {{ item.time_text }}
              </li>
              <li>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                  <path d="M12 2a8 8 0 0 0-8 8c0 5.25 8 12 8 12s8-6.75 8-12a8 8 0 0 0-8-8z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                {{ item.venue_name + " (" + item.type_name + ")" }}
              </li>
            </ul>
            <div v-if="item.price > 0" class="mbr-booking__card-price">
              <span>{{ $t('booking.paid') }}</span>
              <strong>{{ item.price_format }}</strong>
            </div>
          </article>

          <section v-if="!bookingList.length" class="mbr-booking__empty">
            <span class="mbr-booking__empty-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </span>
            <p class="mbr-booking__empty-title">{{ type == 3 ? $t('booking.no_past') : $t('booking.no_upcoming') }}</p>
            <p class="mbr-booking__empty-text">{{ $t('booking.empty_text') }}</p>
          </section>
        </template>
      </main>
    </div>
    <div class="position-fixed top-1 end-1 z-index-2">
      <material-snackbar
        v-if="snackbar === 'danger' || snackbar === 'success'"
        title=""
        date=""
        :description="message"
        :icon="{ component: 'campaign', color: 'white' }"
        :color="snackbar"
        :close-handler="closeSnackbar"
      />
    </div>
    <material-loading :active="loaderActive" />
    <ModalConfirmDonate
      :amount="donateAmount"
      :btnYes="$t('common.yes')"
      :sycn-profile="processPayment"
    />
  </div>
</template>

<script>
import MaterialLoading from "@/components/MaterialLoading.vue";
import { mapMutations, mapActions } from "vuex";
import MaterialSnackbar from "@/components/MaterialSnackbar.vue";
import BookingService from "../services/booking.service";
import "bootstrap/dist/js/bootstrap.bundle";
import ModalConfirmDonate from "@/components/ModalConfirmDonate.vue";
import moment from "moment";
import MemberService from "../services/member.service";
import { getMerchantCode } from "@/config/utils";
import { isEnglish, formatDate as formatLocaleDate } from "@/i18n";
import { errorMessage } from "@/utils/errorMessage";

export default {
  name: "my-transacions",
  components: {
    MaterialSnackbar,
    MaterialLoading,
    ModalConfirmDonate,
  },
  data() {
    return {
      isLoadDone: false,
      merchantCode: "",
      loaderActive: false,
      snackbar: "",
      message: "",
      type: 1,
      dateSelect: null,
      venues: [],
      branches: [],
      branchIdSelect: this.$route.query.branch_id ?? 0,
      showBranchPicker: false,
      keyReloadSelectTimeSlot: 'keyReloadSelectTimeSlot',
      bookingList: [],
    };
  },
  async created() {
    this.isLoadDone = false;
    this.getMemberInfo();
    await this.getBranches();

    if (this.branchIdSelect) {
      await this.getListVenue();
    }
    this.isLoadDone = true;
  },
  // draws its own header like the design: hide the top navbar, and keep the
  // sidenav as an off-screen drawer (same as Donation)
  beforeMount() {
    this.toggleNavbarOnly();
    this.toggleHideConfig();
    const app = document.querySelector("#app");
    app.classList.add("mbr-sidenav-overlay");
    app.classList.remove("g-sidenav-pinned");
  },
  beforeUnmount() {
    document.removeEventListener("mousedown", this.closeBranchPickerOnOutside);
    document.removeEventListener("keydown", this.closeBranchPickerOnEsc);
    this.toggleNavbarOnly();
    this.toggleHideConfig();
    const app = document.querySelector("#app");
    app.classList.remove("mbr-sidenav-overlay");
    if (window.innerWidth > 1200) {
      app.classList.add("g-sidenav-pinned");
    }
  },
  mounted() {
    const code = getMerchantCode();
    this.merchantCode = code ? `/${code}` : "";
    document.addEventListener("mousedown", this.closeBranchPickerOnOutside);
    document.addEventListener("keydown", this.closeBranchPickerOnEsc);
  },
  computed: {
    // computed (not data) so the labels follow a language change
    tabs() {
      return [
        { type: 1, historyType: 1, label: this.$t("booking.tab_book") },
        { type: 2, historyType: 1, label: this.$t("booking.tab_active") },
        { type: 3, historyType: 2, label: this.$t("booking.tab_history") },
      ];
    },
    styleObject() {
      return {
        "--button-color": this.$store.state.buttonColor,
        "--label-color": this.$store.state.labelColor
      };
    },
    branchName() {
      const branch = this.branches.find((item) => item.value == this.branchIdSelect);
      return branch ? branch.label : "";
    },
  },
  methods: {
    ...mapMutations(["toggleNavbarOnly", "toggleHideConfig"]),
    ...mapActions(["setHeaderColor", "setLabelColor", "setButtonColor"]),

    goBack() {
      this.$router.push(this.merchantCode + "/account");
    },

    formatDate(date) {
      if (!isEnglish()) {
        return formatLocaleDate(moment(date).toDate(), { weekday: "long", day: "numeric", month: "long", year: "numeric" });
      }
      return moment(date).format("dddd, DD MMMM YYYY");
    },

    // lowest type price of a venue, e.g. "From $ 10.00"; "Free" only when every type is free
    fromPrice(venue) {
      const prices = (venue.types || []).map((item) => parseFloat(item.price) || 0);
      if (!prices.length) {
        return "";
      }
      if (Math.max(...prices) <= 0) {
        return this.$t("booking.free");
      }
      return this.$t("booking.from_price", { price: "$ " + Math.min(...prices).toFixed(2) });
    },

    openBranchSelect() {
      this.showBranchPicker = true;
    },

    // MBR114 the dropdown is not modal, so close it on any click outside the pill
    closeBranchPickerOnOutside(event) {
      const wrap = this.$refs.branchPicker;
      if (this.showBranchPicker && wrap && !wrap.contains(event.target)) {
        this.showBranchPicker = false;
      }
    },

    closeBranchPickerOnEsc(event) {
      if (event.key === "Escape") {
        this.showBranchPicker = false;
      }
    },

    async changeType(type, history_type = 1) {
      this.type = type;
      this.showBranchPicker = false;
      this.branches = [];
      this.branchIdSelect = 0;
      this.venues = [];

      if (type == 1) {
        await this.getBranches();
        // the venue list was never reloaded when coming back to this tab
        if (this.branchIdSelect) {
          this.getListVenue();
        }
      } else {
        this.memberBookingHistory(history_type);
      }
    },

    async getBranches() {
      this.showLoader();
      await BookingService.getBranches().then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.branches = response.data.data;

            if (this.branches.length > 0) {
              this.branchIdSelect = this.branches[0].value ?? 0;
            }
          } else {
            this.message = errorMessage(response.data.result.message);
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = errorMessage(error);
          this.hideLoader();
        }
      );
    },

    selectBranch(branchId) {
      this.showBranchPicker = false;
      if (branchId == this.branchIdSelect) {
        return;
      }
      this.branchIdSelect = branchId;
      this.getListVenue();
    },

    async getListVenue() {
      this.showLoader();
      var dataForm = {
        branch_id: this.branchIdSelect,
      };

      await BookingService.getListVenue(dataForm).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.venues = response.data.data;
          } else {
            this.snackbar = "danger";
            this.message = errorMessage(response.data.result.message);
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = errorMessage(error);
          this.setTimeOutNotification();
          this.hideLoader();
        }
      );
    },

    memberBookingHistory(type) {
      this.bookingList = [];
      this.showLoader();
      var dataForm = {
        type: type,
      };

      BookingService.memberBookingHistory(dataForm).then(
        (response) => {
          console.log(response);
          if (response.data.result.isSuccess) {
            this.bookingList = response.data.data;
          } else {
            this.snackbar = "danger";
            this.message = errorMessage(response.data.result.message);
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = errorMessage(error);
          this.setTimeOutNotification();
          this.hideLoader();
        }
      );
    },

    selectVenue(id) {
      this.$router.push(this.merchantCode + '/booking/venue/' + id + '?branch_id=' + this.branchIdSelect);
    },

    showLoader() {
      this.loaderActive = true;
    },

    hideLoader() {
      this.loaderActive = false;
    },

    closeSnackbar() {
      this.snackbar = null;
    },

    setTimeOutNotification() {
      setTimeout(() => {
        this.snackbar = null;
      }, 3000);
    },
    getMemberInfo() {
      MemberService.getProfile().then(
          (response) => {
              if (response.data.result.isSuccess) {
                if(response.data.data && !response.data.data.phone_number) {
                  this.$router.push(this.merchantCode + "/profile");
                }
              }
          },
          (error) => {
            console.log("error", error);
          }
      );
    },
  },
};
</script>
<style scoped>
/* MBR063 Booking */
.mbr-booking.page-header {
  overflow: visible;
  overflow-x: clip;
  font-family: var(--mbr-font-body);
  color: var(--mbr-color-ink-headline);
}

/* custom.css still carries the legacy `* { font-family: Poppins }` */
.mbr-booking :where(:not(.material-icons-round)) {
  font-family: var(--mbr-font-body);
}

.mbr-booking .mask {
  background: var(--mbr-color-canvas) !important;
  opacity: 1;
}

.mbr-booking .container {
  max-width: 440px;
  margin: 0 auto;
}

/* MBR064 header + tabs */
.mbr-booking__bar {
  position: sticky;
  top: 0;
  z-index: 20;
  padding: 32px 20px 12px;
  background: rgba(var(--mbr-color-canvas-rgb), 0.95);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.mbr-booking__head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.mbr-booking__back {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  background: var(--mbr-color-surface);
  border: 1px solid var(--mbr-color-outline);
  border-radius: var(--mbr-radius-full);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
  color: var(--mbr-color-primary);
  transition: transform 0.15s ease;
}

.mbr-booking__back:active {
  transform: scale(0.95);
}

.mbr-booking__back svg {
  width: 16px;
  height: 16px;
}

/* font-family/color set explicitly: Material Dashboard forces Roboto Slab on h1-h3 */
.mbr-booking__title {
  margin: 0;
  font-family: var(--mbr-font-headline) !important;
  font-size: 28px;
  font-weight: 800;
  line-height: 36px;
  letter-spacing: -0.02em;
  color: var(--mbr-color-ink-strong);
}

.mbr-booking__segment {
  display: flex;
  padding: 4px;
  background: var(--mbr-color-surface);
  border: 1px solid var(--mbr-color-outline);
  border-radius: var(--mbr-radius-full);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
}

.mbr-booking__seg {
  flex: 1 1 0;
  min-width: 0;
  padding: 8px 4px;
  background: none;
  border: 0;
  border-radius: var(--mbr-radius-full);
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  color: var(--mbr-color-ink-secondary);
  transition: background 0.15s ease, color 0.15s ease;
}

.mbr-booking__seg--active {
  background: var(--mbr-color-primary);
  font-weight: 700;
  color: var(--mbr-color-on-primary);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.12);
}

.mbr-booking__back:focus-visible,
.mbr-booking__seg:focus-visible,
.mbr-booking__book:focus-visible,
.mbr-booking__empty-btn:focus-visible,
.mbr-booking__branch:focus-visible {
  outline: 2px solid var(--mbr-color-primary);
  outline-offset: 2px;
}

.mbr-booking__body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px 20px 0;
}

/* MBR064 branch selector, MBR114 custom dropdown */
.mbr-booking__branch-wrap {
  position: relative;
  z-index: 3;
}

.mbr-booking__branch {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  padding: 10px 16px;
  background: var(--mbr-color-tint);
  border: 1px solid var(--mbr-color-outline);
  border-radius: var(--mbr-radius-full);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
  color: var(--mbr-color-primary);
  text-align: left;
  cursor: pointer;
  -webkit-appearance: none;
  appearance: none;
}

.mbr-booking__branch svg {
  flex: none;
  width: 16px;
  height: 16px;
}

.mbr-booking__branch-chev {
  color: var(--mbr-color-ink-secondary);
}

.mbr-booking__branch-name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  color: var(--mbr-color-ink-strong);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.mbr-booking__branch-chev {
  transition: transform 0.2s ease;
}

.mbr-booking__branch--open .mbr-booking__branch-chev {
  transform: rotate(180deg);
}

.mbr-booking__branch-list {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 4;
  max-height: 240px;
  overflow-y: auto;
  margin: 0;
  padding: 6px;
  list-style: none;
  /* --mbr-color-card is white at 30%, which would let the venue cards show through */
  background: var(--mbr-color-surface);
  border: 1px solid var(--mbr-color-outline);
  border-radius: 20px;
  box-shadow: 0 12px 28px rgba(var(--mbr-color-primary-rgb), 0.16);
}

.mbr-booking__branch-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  border: 0;
  border-radius: 14px;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;
  text-align: left;
  color: var(--mbr-color-ink-headline);
  cursor: pointer;
}

.mbr-booking__branch-option svg {
  flex: none;
  width: 16px;
  height: 16px;
  color: var(--mbr-color-primary);
}

.mbr-booking__branch-option--active {
  background: var(--mbr-color-tint);
  color: var(--mbr-color-ink-strong);
}

.mbr-booking__branch-option:focus-visible {
  outline: 2px solid var(--mbr-color-primary);
  outline-offset: -2px;
}

.mbr-booking-drop-enter-active,
.mbr-booking-drop-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.mbr-booking-drop-enter-from,
.mbr-booking-drop-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.mbr-booking__eyebrow {
  margin: 4px 0 -4px;
  font-size: 11px;
  font-weight: 700;
  line-height: 14px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--mbr-color-ink-secondary);
}

/* MBR064 venue card */
.mbr-booking__venue,
.mbr-booking__card {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: var(--mbr-color-card);
  border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.6);
  border-radius: 24px;
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
}

.mbr-booking__venue {
  cursor: pointer;
}

.mbr-booking__venue-img {
  height: 144px;
  overflow: hidden;
  background: var(--mbr-color-ink-headline);
  border-radius: 12px;
}

.mbr-booking__venue-img img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.9;
}

.mbr-booking__venue-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.mbr-booking__venue-name {
  min-width: 0;
  margin: 0;
  font-family: var(--mbr-font-headline) !important;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  letter-spacing: normal;
  color: var(--mbr-color-ink-strong);
  overflow-wrap: anywhere;
}

.mbr-booking__venue-price {
  flex: none;
  padding: 2px 8px;
  background: var(--mbr-color-peach);
  border-radius: var(--mbr-radius-full);
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  color: var(--mbr-color-primary);
}

.mbr-booking__book {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  height: 44px;
  margin-top: 4px;
  border: 0;
  border-radius: var(--mbr-radius-full);
  background: var(--mbr-color-primary);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.12);
  font-family: var(--mbr-font-headline) !important;
  font-size: 12px;
  font-weight: 700;
  color: var(--mbr-color-on-primary);
  transition: transform 0.15s ease, background-color 0.15s ease;
}

.mbr-booking__book:hover {
  background: var(--mbr-color-primary-light);
}

.mbr-booking__book:active {
  transform: scale(0.98);
}

.mbr-booking__book svg {
  width: 14px;
  height: 14px;
}

/* empty states */
.mbr-booking__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  background: var(--mbr-color-card);
  border: 1px dashed var(--mbr-color-outline);
  border-radius: 24px;
  text-align: center;
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
}

.mbr-booking__empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  margin-bottom: 12px;
  background: var(--mbr-color-peach);
  border-radius: var(--mbr-radius-full);
  box-shadow: inset 0 2px 4px rgba(var(--mbr-color-primary-rgb), 0.06);
  color: var(--mbr-color-primary);
}

.mbr-booking__empty-icon svg {
  width: 32px;
  height: 32px;
}

.mbr-booking__empty-title {
  margin: 0 0 4px;
  font-family: var(--mbr-font-headline) !important;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  color: var(--mbr-color-ink-strong);
}

.mbr-booking__empty-text {
  max-width: 220px;
  margin: 0;
  font-size: 12px;
  line-height: 16px;
  color: rgba(var(--mbr-color-ink-secondary-rgb), 0.75);
}

.mbr-booking__empty-btn {
  margin-top: 16px;
  padding: 10px 24px;
  background: var(--mbr-color-surface);
  border: 1.5px solid var(--mbr-color-outline);
  border-radius: var(--mbr-radius-full);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
  font-size: 12px;
  font-weight: 700;
  color: var(--mbr-color-ink-headline);
}

.mbr-booking__empty-btn:hover {
  background: var(--mbr-color-tint);
}

/* MBR065 booking card */
.mbr-booking__card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.mbr-booking__code {
  min-width: 0;
  font-family: var(--mbr-font-headline) !important;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  color: var(--mbr-color-ink-strong);
  overflow-wrap: anywhere;
}

.mbr-booking__status {
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background: rgba(var(--mbr-color-positive-rgb), 0.1);
  border-radius: var(--mbr-radius-full);
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  color: var(--mbr-color-positive);
}

.mbr-booking__status--done {
  background: var(--mbr-color-tint);
  color: var(--mbr-color-ink-secondary);
}

.mbr-booking__status svg {
  width: 12px;
  height: 12px;
}

.mbr-booking__meta {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.mbr-booking__meta li {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
  color: var(--mbr-color-ink-secondary);
}

.mbr-booking__meta svg {
  flex: none;
  width: 16px;
  height: 16px;
  color: var(--mbr-color-primary);
}

.mbr-booking__card-price {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 12px;
  border-top: 1px solid rgba(var(--mbr-color-outline-rgb), 0.5);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--mbr-color-ink-secondary);
}

.mbr-booking__card-price strong {
  font-family: var(--mbr-font-headline) !important;
  font-size: 16px;
  letter-spacing: -0.01em;
  text-transform: none;
  color: var(--mbr-color-ink-strong);
}
</style>
